/**
 * La portada, convertida en TABLERO.
 *
 * QUÉ CAMBIÓ Y POR QUÉ. Antes esta pantalla era texto explicativo: contaba cómo
 * funciona el sistema y terminaba con el diagnóstico del backend. Explicar la
 * mecánica sigue haciendo falta —la sección «Cómo se usa» se queda entera—, pero
 * no es lo que viene a resolver alguien que ABRE la aplicación. La pregunta al
 * entrar es «¿qué puedo hacer ahora?», y esa se responde con datos, no con
 * párrafos: si tengo algo reservado, si hay avisos esperándome, si hoy la
 * escuela abre y si queda algo libre.
 *
 * TRES REGLAS QUE ESTA PANTALLA NO PUEDE ROMPER:
 *
 * 1. NINGÚN ENDPOINT NUEVO. Las cuatro tarjetas se arman con lo que el API ya
 *    sirve: `/api/mis-reservaciones`, `/api/mis/notificaciones`,
 *    `/api/disponibilidad` y `/api/configuracion`. Las tres primeras comparten
 *    clave de TanStack Query con las pantallas que ya las piden —la campana del
 *    encabezado, «Mis reservaciones», «Disponibilidad»—, así que el tablero
 *    normalmente no cuesta ni una petición extra y, al saltar a esas pantallas,
 *    los datos ya están en caché.
 *
 * 2. LO QUE SE VE DEPENDE DE LOS PERMISOS, NO DEL ROL (ADR-0007, regla 8). Un
 *    alumno no ve nada de administración; quien puede cargar horarios ve el
 *    aviso que a un alumno no le sirve de nada. Se pregunta con `tienePermiso`.
 *
 * 3. NO SE INVENTA NINGÚN DATO DE LA INSTITUCIÓN (regla 2). Cuántas
 *    reservaciones caben a la vez, a qué hora abre la escuela y qué días no hay
 *    servicio son datos que carga la escuela. Lo que no llega se muestra como
 *    HUECO DECLARADO —`.sin-dato`—, nunca como un cero: un «0 espacios libres»
 *    donde en realidad no se pudo consultar es una mentira con formato de dato.
 *
 * EL GRITO DEL HORARIO. Mientras `horarios_operacion` esté vacía el motor cierra
 * todos los días a propósito (ADR-0005, decisión 2) y NADIE puede reservar. Eso
 * no es un detalle de configuración: es lo único que impide que el sistema
 * sirva, y quien puede arreglarlo tiene que verlo al entrar, no al navegar hasta
 * `/admin`. Por eso el aviso va a ancho completo y por delante del tablero, y
 * solo para quien tiene `administrar_horarios`: a un alumno el mismo texto le
 * pediría algo que no puede hacer.
 *
 * QUÉ SE REPITE DEL ENCABEZADO Y QUÉ NO. La sección de accesos sí repite
 * destinos —«Mostrador», «Administración», «Mis reservaciones»—, y eso es el
 * punto: cada entrada trae qué se hace al llegar, que es justo lo que a un menú
 * le falta. Dos cosas quedan fuera:
 *
 *   · «AVISOS». El enlace del encabezado lleva la CUENTA en su nombre accesible
 *     («Avisos, 3 sin leer»). Un segundo enlace llamado igual pero sin el número
 *     compite con él en cuanto alguien navega por voz o por lista de enlaces, y
 *     además rompería las pruebas de navegador, que localizan por ese nombre. La
 *     tarjeta da el número y remite a la campana.
 *   · «ENTRAR CON GOOGLE». Es una acción con estado —se deshabilita mientras
 *     abre Google—, no un destino; dos copias podrían verse distintas a la vez.
 *     Sigue en el encabezado, y la primera tarjeta explica que hace falta.
 */

import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { consultarSalud } from '../api/cliente'
import type { EstadoBackend } from '../api/cliente'
import { Estado } from '../componentes/Estado'
import type { TonoEstado } from '../componentes/Estado'
import { NOMBRE_SISTEMA } from '../componentes/Encabezado'
import { Ayuda } from '../componentes/Ayuda'
import { EsqueletoDeTarjeta } from '../componentes/Esqueleto'
import { Tour, relanzarTour } from '../componentes/Tour'
import type { PasoDelTour } from '../componentes/Tour'
import { obtenerConfiguracion } from '../api/configuracion'
import {
  claveDeDisponibilidad,
  consultarDisponibilidad,
} from '../api/disponibilidad'
import type { Disponibilidad } from '../api/disponibilidad'
import {
  CLAVE_MIS_RESERVACIONES,
  consultarMisReservaciones,
} from '../api/reservaciones'
import type { MiReservacion } from '../api/reservaciones'
import { CLAVE_NOTIFICACIONES, consultarAvisos } from '../api/notificaciones'
import { esZonaHorariaReconocida } from '../api/contrato'
import type { Perfil, UsuarioRegistrado } from '../api/perfil'
import { usePerfil } from '../sesion/usePerfil'
import { puedeAdministrar, tienePermiso } from '../sesion/SoloAdministrador'
import { puedeAtenderMostrador } from '../sesion/SoloMostrador'
import {
  conInicialMayuscula,
  useFormatoDeDia,
  useFormatoDeHora,
} from '../formato/fechas'
import { fechaDeHoy } from './busqueda-disponibilidad'

const TITULOS: Record<EstadoBackend['clase'], string> = {
  listo: 'El backend responde y la base de datos está disponible.',
  'sin-base': 'El backend responde, pero la base de datos no está disponible.',
  'sin-respuesta': 'El backend no responde.',
}

const TONOS: Record<EstadoBackend['clase'], TonoEstado> = {
  listo: 'ok',
  'sin-base': 'aviso',
  'sin-respuesta': 'error',
}

/**
 * Zona de respaldo cuando el servidor no reporta la suya, igual que en «Mis
 * reservaciones» y por la misma razón: la zona del NAVEGADOR no se usa nunca
 * (ADR-0004). Quien consulte desde otra zona vería horas que no son las de la
 * escuela, sin que nada avisara. Cuando se usa este respaldo, la hora sale
 * etiquetada con la zona.
 */
const ZONA_SIN_DATO = 'UTC'

/** Basta con medio minuto: la frontera que importa es la hora en punto. */
const INTERVALO_RELOJ_MS = 30_000

/**
 * El recorrido guiado de la portada.
 *
 * TODOS LOS SELECTORES APUNTAN A ELEMENTOS DE ESTA MISMA PANTALLA, y eso no es
 * casual. La tentación era señalar los enlaces del encabezado —«ahí está
 * Disponibilidad»—, pero en celular esos enlaces viven dentro del panel plegado:
 * siguen en el DOM, así que `Tour` los daría por existentes, y `getBoundingClientRect()`
 * de un elemento con `display: none` devuelve ceros. El resultado sería un realce
 * de 0×0 en la esquina superior izquierda señalando a nada, justo en el
 * dispositivo donde más falta hace el recorrido.
 *
 * NO SE AFIRMA NADA DE LA INSTITUCIÓN (regla 2 del proyecto). Ni horarios, ni
 * cuántas reservaciones caben, ni qué días abre: todo eso son datos que carga la
 * escuela y que este texto no puede adelantar. Lo que se explica es la MECÁNICA
 * del sistema, que sí es fija.
 *
 * Se declara fuera del componente para que la referencia del arreglo sea estable
 * entre renders: `Tour` la usa como dependencia de sus efectos.
 */
const PASOS: PasoDelTour[] = [
  {
    /* El título de la página y no la tarjeta de abajo, a propósito: `Tour`
       coloca su ficha ENCIMA del elemento cuando debajo no cabe, y encima de una
       tarjeta alta eso significa encima del encabezado, tapando la navegación
       que el propio recorrido está explicando. Un elemento bajo y alto en la
       página garantiza que la ficha caiga debajo. */
    selector: '.titulo-pagina',
    titulo: 'El sistema en cuatro pasos',
    texto:
      'Este recorrido señala, uno por uno, los cuatro momentos de reservar un espacio. Puedes cerrarlo cuando quieras y volver a lanzarlo desde el final de esta tarjeta.',
  },
  {
    selector: '#paso-disponibilidad',
    titulo: 'Primero se mira la disponibilidad',
    texto:
      'En «Disponibilidad» eliges una fecha y ves, espacio por espacio, los bloques de una hora y si están libres, ocupados o bloqueados.',
  },
  {
    selector: '#paso-reservar',
    titulo: 'Reservar es pulsar un bloque libre',
    texto:
      'Cada bloque libre trae su propio botón «Reservar». La confirmación dice qué espacio, qué lugar y qué horas quedaron a tu nombre.',
  },
  {
    selector: '#paso-mis-reservaciones',
    titulo: 'Todo queda en «Mis reservaciones»',
    texto:
      'Ahí está lo que reservaste, con su estado, y es el único sitio desde donde se cancela. Las canceladas se quedan a la vista, marcadas.',
  },
  {
    selector: '#paso-avisos',
    titulo: 'Los avisos llegan a la campana',
    texto:
      'Las confirmaciones y los recordatorios aparecen en «Mis avisos», y el encabezado lleva la cuenta de los que todavía no has leído.',
  },
  {
    selector: '#relanzar-recorrido',
    titulo: 'Se puede volver a ver',
    texto:
      'El recorrido se muestra una sola vez por navegador. Este botón lo lanza otra vez desde el principio, las veces que haga falta.',
  },
]

export function Inicio() {
  // El nombre de la institución sale de la configuración del servidor, no del
  // código (ADR-0004). Ver apps/web/src/api/configuracion.ts.
  const { data: configuracion } = useQuery({
    queryKey: ['configuracion'],
    queryFn: obtenerConfiguracion,
    staleTime: 10 * 60 * 1000,
  })

  // La misma clave que usan el encabezado y los guardianes de ruta: una sola
  // petición abastece a todos y el tablero ve exactamente el mismo estado de
  // sesión que la navegación.
  const perfil = usePerfil()

  const consulta = useQuery({
    queryKey: ['salud'],
    queryFn: consultarSalud,
    // consultarSalud() no lanza: los tres desenlaces son datos, no fallas.
    retry: false,
    refetchInterval: 30_000,
  })

  return (
    <>
      <ESTILOS />
      <h1 className="titulo-pagina">{NOMBRE_SISTEMA}</h1>
      <p className="parrafo-guia">
        {configuracion?.institucion_nombre
          ? `${configuracion.institucion_nombre}. `
          : ''}
        Ya se puede consultar la disponibilidad de cada día y reservar un
        bloque. El sistema sigue en desarrollo: corre en red local y no se ha
        publicado a internet.
      </p>

      <Tablero
        perfil={perfil.data}
        // `usePerfil` no reintenta, así que un fallo se queda quieto. Sin este
        // dato las tarjetas personales se quedarían con el esqueleto puesto
        // para siempre, que es indistinguible de una pantalla rota.
        sesionFallo={perfil.isError}
        zonaDeConfiguracion={configuracion?.zona_horaria ?? null}
      />

      <Accesos perfil={perfil.data} />

      {/*
        LA PORTADA EMPIEZA POR LO QUE VIENE UN ALUMNO A HACER. Antes lo primero
        —y lo único— era el estado del backend, que es un diagnóstico de quien
        opera el sistema, no una respuesta a «¿cómo reservo?». El diagnóstico
        sigue abajo, entero; lo que cambia es el orden.

        Los cuatro pasos NO enlazan a «Mis reservaciones» ni a «Mis avisos» a
        propósito: las dos exigen registro completo, y ofrecer un camino que va a
        terminar en «completa tu registro» se lee como una aplicación rota. Se
        nombran, y el encabezado las ofrece a quien sí puede entrar.
      */}
      <section
        className="tarjeta seccion-portada"
        id="como-se-usa"
        aria-labelledby="titulo-como-se-usa"
      >
        <h2 className="tarjeta__titulo" id="titulo-como-se-usa">
          Cómo se usa
        </h2>

        <ol className="pasos-uso">
          <li id="paso-disponibilidad">
            <span className="pasos-uso__que">Mira la disponibilidad del día.</span>{' '}
            Eliges una fecha y aparecen, espacio por espacio, los bloques de una
            hora con su estado.
            <span className="pasos-uso__accion">
              <Link
                to="/disponibilidad"
                search={{ fecha: fechaDeHoy() }}
                className="boton boton--primario"
              >
                Ver la disponibilidad de hoy
              </Link>
            </span>
          </li>

          <li id="paso-reservar">
            <span className="pasos-uso__que">Reserva un bloque libre.</span> Cada
            bloque libre trae su botón, y la confirmación dice qué espacio, qué
            lugar y qué horas quedaron a tu nombre.
          </li>

          <li id="paso-mis-reservaciones">
            <span className="pasos-uso__que">
              Consulta o cancela en «Mis reservaciones».
            </span>{' '}
            Es el único sitio desde donde se cancela, y las canceladas se quedan
            a la vista para que puedas comprobarlo.{' '}
            {/* Por qué un «?» aquí: cuántas reservaciones caben a la vez es la
                primera pregunta al toparse con el rechazo, y la respuesta es un
                dato de rol que esta pantalla no puede afirmar. */}
            <Ayuda clave="limite-simultaneas" />
          </li>

          <li id="paso-avisos">
            <span className="pasos-uso__que">Revisa «Mis avisos».</span> Ahí
            llegan las confirmaciones y los recordatorios, y el encabezado lleva
            la cuenta de los que no has leído.{' '}
            {/* Y aquí, porque «sin envío aún» es una promesa que el sistema NO
                está cumpliendo todavía y callarlo haría creer que un aviso
                salió cuando no salió. */}
            <Ayuda clave="aviso-sin-envio" />
          </li>
        </ol>

        <div className="acciones acciones--separadas">
          <button
            type="button"
            className="boton"
            id="relanzar-recorrido"
            onClick={() => relanzarTour()}
          >
            Ver el recorrido guiado
          </button>
        </div>
      </section>

      <section className="tarjeta seccion-portada" aria-labelledby="titulo-estado-backend">
        <h2 className="tarjeta__titulo" id="titulo-estado-backend">
          Estado del backend
        </h2>

        {consulta.data === undefined ? (
          consulta.isError ? (
            <Estado
              tono="error"
              titulo="No se pudo consultar el estado del backend."
              detalle={
                consulta.error instanceof Error
                  ? consulta.error.message
                  : 'Error inesperado en el cliente.'
              }
              enVivo
            />
          ) : (
            <Estado
              tono="neutro"
              titulo="Consultando el estado del backend…"
              detalle="Petición a GET /salud/listo."
              enVivo
            />
          )
        ) : (
          <Estado
            tono={TONOS[consulta.data.clase]}
            titulo={TITULOS[consulta.data.clase]}
            detalle={consulta.data.detalle}
            enVivo
          >
            <button
              type="button"
              className="boton"
              onClick={() => void consulta.refetch()}
              disabled={consulta.isFetching}
            >
              {consulta.isFetching ? 'Consultando…' : 'Volver a consultar'}
            </button>
          </Estado>
        )}
      </section>

      {/* Se monta al final: el recorrido busca sus elementos en cuanto arranca,
          y para entonces todo lo que señala ya está en el documento. */}
      <Tour pasos={PASOS} />
    </>
  )
}

/* --- El tablero ------------------------------------------------------------ */

/**
 * La tira de cuatro tarjetas.
 *
 * SIEMPRE SON CUATRO, con sesión y sin ella. Las dos primeras son personales
 * —lo mío reservado, mis avisos— y sin sesión no quedan vacías: dicen que el
 * dato depende de entrar, que es información y no un hueco decorativo. Un
 * tablero que pasa de tres tarjetas a cuatro cuando termina de cargar el perfil
 * mueve todo lo que hay debajo, y quien iba a pulsar un enlace pulsa otro.
 *
 * NINGUNA TARJETA TUMBA A LAS DEMÁS. Cada consulta resuelve su propio fallo
 * dentro de su tarjeta: que `/api/mis-reservaciones` no responda no puede dejar
 * sin horario a la tarjeta del día.
 */
function Tablero({
  perfil,
  sesionFallo,
  zonaDeConfiguracion,
}: {
  perfil: Perfil | undefined
  /** `/api/perfil` falló: no se sabe si hay sesión. No es lo mismo que «no hay». */
  sesionFallo: boolean
  zonaDeConfiguracion: string | null
}) {
  const ahora = useAhora(INTERVALO_RELOJ_MS)

  /* Se recalcula en cada render a propósito, aunque sea clave de consulta:
     TanStack Query compara las claves por VALOR, así que repetir la misma
     cadena no provoca ni una petición. Fijarla al montar sí tendría un efecto
     indeseable — una pestaña abierta desde anoche seguiría enseñando el día de
     ayer—, y con el reloj de arriba latiendo, a medianoche la clave cambia sola
     y el tablero pasa de día. */
  const hoy = fechaDeHoy()

  const usuario = perfil?.estado === 'registrado' ? perfil.usuario : null
  const conSesion = usuario !== null

  /* MISMA CLAVE QUE LA PANTALLA DE DISPONIBILIDAD, con espacio sin filtrar. Así
     el tablero y `/disponibilidad?fecha=hoy` comparten una sola respuesta: al
     saltar de aquí allá, la pantalla ya tiene los datos. */
  const dia = useQuery({
    queryKey: claveDeDisponibilidad(hoy, undefined),
    queryFn: () => consultarDisponibilidad({ fecha: hoy }),
    staleTime: 60_000,
    retry: 1,
  })

  const reservaciones = useQuery({
    queryKey: CLAVE_MIS_RESERVACIONES,
    queryFn: consultarMisReservaciones,
    // Sin registro completo la ruta responde 403: pedirla sería fabricar un
    // error para luego esconderlo.
    enabled: conSesion,
    retry: 1,
  })

  /* La misma clave que la campana del encabezado. NO se le pone
     `refetchInterval`: la campana ya refresca esa clave cada minuto y las dos
     leen la misma caché; dos intervalos sobre la misma clave solo duplicarían
     peticiones. */
  const avisos = useQuery({
    queryKey: CLAVE_NOTIFICACIONES,
    queryFn: () => consultarAvisos(),
    enabled: conSesion,
    retry: false,
  })

  /* La zona horaria del SISTEMA, nunca la del navegador. Primero la de
     `/api/configuracion`; si no llegó, la que reporta `/api/disponibilidad`,
     que sale de la misma tabla. Se comprueba con `Intl` antes de formatear:
     una zona desconocida haría lanzar al formateador y tumbaría la tarjeta. */
  const zonaReportada = zonaDeConfiguracion ?? dia.data?.zonaHoraria ?? null
  const zonaValida =
    zonaReportada !== null && esZonaHorariaReconocida(zonaReportada)
      ? zonaReportada
      : null
  const zona = zonaValida ?? ZONA_SIN_DATO
  const formatoHora = useFormatoDeHora(zona)
  const formatoDia = useFormatoDeDia(zona)

  return (
    <section className="tablero-envoltura" aria-labelledby="titulo-tablero">
      {/* El título existe para el árbol de encabezados —de `h1` a `h3` sin
          saltos—, pero no se pinta: cuatro tarjetas rotuladas una por una no
          necesitan que además se les anuncie que son un tablero. */}
      <h2 className="visualmente-oculto" id="titulo-tablero">
        Tu situación de un vistazo
      </h2>

      {/* EL GRITO. Solo para quien puede apagarlo; ver el encabezado del
          archivo. Va antes que el tablero porque mientras esto siga así, el
          resto de las tarjetas describen un sistema que no acepta nada. */}
      {usuario !== null &&
        tienePermiso(usuario, 'administrar_horarios') &&
        dia.data?.motivo === 'sin_horario' && (
          <div className="tablero__grito">
            <Estado
              tono="error"
              titulo="Nadie puede reservar: falta cargar el horario de operación."
              detalle="El motor cierra todos los días mientras no haya horario vigente, así que ninguna reservación se acepta —ni la tuya ni la de nadie—. Capturarlo es lo que enciende el sistema."
              enVivo
            >
              <Link
                to="/admin"
                hash="titulo-horarios"
                className="boton boton--primario"
              >
                Cargar el horario de operación
              </Link>
              {/* La ayuda va como hijo y no dentro de `detalle`: `Estado` pinta
                  el detalle en un `<p>`, y el globo de `Ayuda` es un `<div>`.
                  Un `div` dentro de un `p` es marcado inválido. */}
              <Ayuda clave="horario-de-operacion" />
            </Estado>
          </div>
        )}

      <ul className="tablero">
        <TarjetaMiReservacion
          perfil={perfil}
          sesionFallo={sesionFallo}
          consulta={reservaciones}
          ahora={ahora}
          formatoDia={formatoDia}
          formatoHora={formatoHora}
          zonaEtiquetada={zonaValida === null ? zona : null}
        />

        <TarjetaAvisos
          conSesion={conSesion}
          sesionFallo={sesionFallo}
          consulta={avisos}
        />

        <TarjetaDelDia consulta={dia} usuario={usuario} />

        <TarjetaEspaciosLibres consulta={dia} ahora={ahora} />
      </ul>
    </section>
  )
}

/** El molde de una tarjeta. El título es lo que la nombra para quien no la ve. */
function TarjetaDelTablero({
  titulo,
  children,
}: {
  titulo: string
  children: ReactNode
}) {
  return (
    <li className="tarjeta">
      <h3 className="tablero__titulo">{titulo}</h3>
      {children}
    </li>
  )
}

/** Mientras carga, la forma de lo que viene: ver el encabezado de `Esqueleto`. */
function TarjetaCargando({ etiqueta }: { etiqueta: string }) {
  return (
    <li>
      <EsqueletoDeTarjeta lineas={2} etiqueta={etiqueta} />
    </li>
  )
}

/**
 * Lo que tengo reservado ahora mismo, o lo siguiente que tengo.
 *
 * VIGENTE ES LO MISMO QUE EN «MIS RESERVACIONES»: `confirmada` o `en_curso`, y
 * que su hora de fin no haya pasado. Cualquier otra definición dejaría al
 * tablero afirmando que hay algo activo cuando la otra pantalla dice que no.
 *
 * CUÁNTAS PUEDO TENER A LA VEZ ES UN HUECO DECLARADO, no un cero. El límite lo
 * fija el rol y vive en la tabla `roles`, pero el API solo lo entrega en
 * `/api/admin/roles`, tras `administrar_permisos`: un alumno no puede leerlo. En
 * vez de inventar un número —o peor, de suponer «una»— se dice que lo fija el
 * rol y el «?» explica el concepto.
 */
function TarjetaMiReservacion({
  perfil,
  sesionFallo,
  consulta,
  ahora,
  formatoDia,
  formatoHora,
  zonaEtiquetada,
}: {
  perfil: Perfil | undefined
  sesionFallo: boolean
  consulta: {
    data: MiReservacion[] | undefined
    isPending: boolean
    isError: boolean
  }
  ahora: Date
  formatoDia: Intl.DateTimeFormat
  formatoHora: Intl.DateTimeFormat
  zonaEtiquetada: string | null
}) {
  if (perfil === undefined) {
    /* Sin perfil hay dos situaciones distintas y solo una es esperar. El
       esqueleto es para la espera corta; un fallo pide palabras. */
    if (!sesionFallo) {
      return <TarjetaCargando etiqueta="Consultando tu sesión…" />
    }
    return (
      <TarjetaDelTablero titulo="Tu reservación">
        <p className="sin-dato">No se pudo consultar tu sesión.</p>
        <p className="tablero__nota">
          Mientras el servidor no responda quién eres, esta tarjeta no puede
          decir si tienes algo reservado. El encabezado lo vuelve a intentar al
          recargar.
        </p>
      </TarjetaDelTablero>
    )
  }

  if (perfil.estado === 'anonimo') {
    return (
      <TarjetaDelTablero titulo="Tu reservación">
        <p className="tablero__dato">Hace falta entrar</p>
        <p className="tablero__nota">
          {perfil.autenticacion_disponible
            ? 'Consultar la disponibilidad no exige sesión; reservar sí. El botón de entrar vive en el encabezado, y en el teléfono dentro de «Menú».'
            : 'Este servidor todavía no tiene configurado el inicio de sesión, así que por ahora solo se puede consultar.'}
        </p>
      </TarjetaDelTablero>
    )
  }

  if (perfil.estado === 'sin_registrar') {
    return (
      <TarjetaDelTablero titulo="Tu reservación">
        <p className="tablero__dato">Falta completar tu registro</p>
        <p className="tablero__nota">
          Entraste con Google, pero todavía no hay una cuenta tuya en el sistema:
          Google no entrega la matrícula y esa la escribes tú.
        </p>
        <p className="tablero__acciones">
          <Link to="/registro" className="boton boton--primario">
            Ir al registro
          </Link>
        </p>
      </TarjetaDelTablero>
    )
  }

  if (consulta.isPending) {
    return <TarjetaCargando etiqueta="Consultando tus reservaciones…" />
  }

  if (consulta.isError || consulta.data === undefined) {
    return (
      <TarjetaDelTablero titulo="Tu reservación">
        <p className="sin-dato">No se pudo consultar.</p>
        <p className="tablero__nota">
          El tablero no sabe si tienes algo reservado. «Mis reservaciones» —el
          acceso está aquí abajo— lo vuelve a intentar y explica qué falló.
        </p>
      </TarjetaDelTablero>
    )
  }

  const vigente = proximaVigente(consulta.data, ahora)

  if (vigente === null) {
    return (
      <TarjetaDelTablero titulo="Tu reservación">
        <p className="tablero__dato">Ninguna activa</p>
        {/* `div` y no `p` porque contiene el globo de `Ayuda`, que es un
            `div`. Ver la nota del grito, más arriba. */}
        <div className="tablero__nota">
          Cuántas puedes tener a la vez lo fija tu rol, y ese número no llega a
          esta pantalla: <span className="sin-dato">sin dato</span>.{' '}
          <Ayuda clave="limite-simultaneas" />
        </div>
        <p className="tablero__acciones">
          <Link
            to="/disponibilidad"
            search={{ fecha: fechaDeHoy() }}
            className="boton boton--primario"
          >
            Buscar un bloque libre
          </Link>
        </p>
      </TarjetaDelTablero>
    )
  }

  const enCurso = vigente.inicio.getTime() <= ahora.getTime()

  return (
    <TarjetaDelTablero titulo={enCurso ? 'Ahora mismo' : 'Tu próxima reservación'}>
      <p className="tablero__dato">
        {vigente.espacioNombre}{' '}
        <span className="tablero__clave">{vigente.espacioId}</span>
      </p>
      <p className="tablero__nota">
        Lugar {vigente.lugar}.{' '}
        {conInicialMayuscula(formatoDia.format(vigente.inicio))}, de{' '}
        {formatoHora.format(vigente.inicio)} a {formatoHora.format(vigente.fin)}
        {/* La zona solo se nombra cuando NO se conoce la del sistema: ahí la
            hora que se está leyendo puede no ser la de la escuela, y callarlo
            sería peor que decirlo. */}
        {zonaEtiquetada !== null
          ? ` (en ${zonaEtiquetada}; el servidor no reportó su zona horaria).`
          : '.'}
      </p>
      {/* SIN ENLACE, por lo mismo que la tarjeta de avisos: «Mis
          reservaciones» ya está en el encabezado y entre los accesos de abajo.
          Un tercer enlace con ese mismo nombre en la misma página no lleva a
          ningún sitio nuevo y sí vuelve ambigua la navegación por voz. */}
    </TarjetaDelTablero>
  )
}

/**
 * Cuántos avisos no he leído.
 *
 * SIN ENLACE, A PROPÓSITO: la campana del encabezado ya lleva a «Avisos» y está
 * en todas las pantallas. Ver el encabezado del archivo.
 *
 * UN FALLO NO SE PINTA COMO CERO. `sin_leer` es un conteo del servidor; si la
 * consulta falló, lo que hay es «no lo sé», y un 0 en su lugar diría justo lo
 * contrario de lo que pasa.
 */
function TarjetaAvisos({
  conSesion,
  sesionFallo,
  consulta,
}: {
  conSesion: boolean
  sesionFallo: boolean
  consulta: {
    data: { sin_leer: number } | undefined
    isPending: boolean
    isError: boolean
  }
}) {
  if (!conSesion) {
    return (
      <TarjetaDelTablero titulo="Tus avisos">
        {/* «No se sabe» y «no hay sesión» no son lo mismo, y la diferencia
            importa: la primera es un fallo que puede arreglarse recargando. */}
        <p className="sin-dato">
          {sesionFallo ? 'No se pudo consultar tu sesión.' : 'Sin sesión'}
        </p>
        <p className="tablero__nota">
          Las confirmaciones y los recordatorios se cuentan aquí en cuanto
          entras. Son de cada cuenta, así que no hay nada que mostrar antes.
        </p>
      </TarjetaDelTablero>
    )
  }

  if (consulta.isPending) {
    return <TarjetaCargando etiqueta="Consultando tus avisos…" />
  }

  if (consulta.isError || consulta.data === undefined) {
    return (
      <TarjetaDelTablero titulo="Tus avisos">
        <p className="sin-dato">No se pudo consultar.</p>
        <p className="tablero__nota">
          La campana del encabezado lo vuelve a intentar sola cada minuto.
        </p>
      </TarjetaDelTablero>
    )
  }

  const sinLeer = consulta.data.sin_leer

  return (
    <TarjetaDelTablero titulo="Tus avisos">
      <p className="tablero__cifra">{sinLeer}</p>
      <p className="tablero__nota">
        {sinLeer === 0
          ? 'Nada sin leer. Los nuevos aparecen en la campana del encabezado.'
          : 'Sin leer. La campana del encabezado los abre.'}
      </p>
    </TarjetaDelTablero>
  )
}

/**
 * Si hoy se puede reservar, y por qué no cuando no.
 *
 * ES LA MISMA RESPUESTA DEL MOTOR QUE USA `/disponibilidad`, resumida: `motivo`
 * distingue día abierto, día no hábil y falta de horario, y esos tres casos
 * significan cosas distintas para quien mira. Un «cerrado» a secas los
 * confundiría, y el tercero es el que puede arreglarse.
 */
function TarjetaDelDia({
  consulta,
  usuario,
}: {
  consulta: {
    data: Disponibilidad | undefined
    isPending: boolean
    isError: boolean
  }
  usuario: UsuarioRegistrado | null
}) {
  if (consulta.isPending) {
    return <TarjetaCargando etiqueta="Consultando el día de hoy…" />
  }

  if (consulta.isError || consulta.data === undefined) {
    return (
      <TarjetaDelTablero titulo="Hoy">
        <p className="sin-dato">No se pudo consultar.</p>
        <p className="tablero__nota">
          El tablero no sabe si hoy la escuela abre. La pantalla de
          disponibilidad lo vuelve a intentar y dice qué falló.
        </p>
      </TarjetaDelTablero>
    )
  }

  const datos = consulta.data

  if (datos.motivo === 'sin_horario') {
    /* A quien administra ya se lo gritó el aviso de arriba; repetir el mismo
       texto en la tarjeta solo sería ruido, así que aquí la nota cambia según
       lo que quien mira puede hacer al respecto. */
    const puedeCargarlo =
      usuario !== null && tienePermiso(usuario, 'administrar_horarios')

    return (
      <TarjetaDelTablero titulo="Hoy">
        <p className="tablero__dato">Cerrado</p>
        <div className="tablero__nota">
          {puedeCargarlo
            ? 'Sigue sin haber horario de operación cargado, y por eso el motor cierra todos los días.'
            : 'La escuela todavía no ha cargado su horario de operación, así que hoy no hay bloques que ofrecer. No tiene que ver con tu cuenta.'}{' '}
          <Ayuda clave="horario-de-operacion" />
        </div>
      </TarjetaDelTablero>
    )
  }

  if (datos.motivo === 'dia_no_habil') {
    return (
      <TarjetaDelTablero titulo="Hoy">
        <p className="tablero__dato">Sin servicio</p>
        <div className="tablero__nota">
          {datos.detalle !== null
            ? `El calendario lo registra como día no hábil: ${datos.detalle}.`
            : 'El calendario lo registra como día no hábil.'}{' '}
          <Ayuda clave="dia-no-habil" />
        </div>
        <p className="tablero__acciones">
          <Link
            to="/disponibilidad"
            search={{ fecha: fechaDeHoy() }}
            className="boton"
          >
            Elegir otra fecha
          </Link>
        </p>
      </TarjetaDelTablero>
    )
  }

  const horario =
    datos.abre !== null && datos.cierra !== null
      ? `${horaCorta(datos.abre)} a ${horaCorta(datos.cierra)}`
      : null

  return (
    <TarjetaDelTablero titulo="Hoy">
      <p className="tablero__dato">Abierto</p>
      <p className="tablero__nota">
        {horario !== null ? (
          <>
            De {horario}, hora de {datos.zonaHoraria}, que es la zona que
            reporta el sistema.
          </>
        ) : (
          <>
            El día está abierto, aunque el servidor no reportó hora de apertura
            ni de cierre: <span className="sin-dato">sin dato</span>.
          </>
        )}
      </p>
      <p className="tablero__acciones">
        <Link
          to="/disponibilidad"
          search={{ fecha: fechaDeHoy() }}
          className="boton"
        >
          Ver los bloques de hoy
        </Link>
      </p>
    </TarjetaDelTablero>
  )
}

/**
 * Cuántos espacios tienen algo libre en lo que queda del día.
 *
 * SE CUENTAN SOLO LOS BLOQUES QUE TODAVÍA NO TERMINARON. Un bloque de las 08:00
 * sigue marcado «libre» a las 15:00 —nadie lo ocupó— pero no se puede reservar,
 * y contarlo daría una cifra que el sistema no va a respetar. Por eso entra el
 * reloj: lo que se ofrece es lo que queda por delante.
 *
 * EL TOTAL SALE DE LA RESPUESTA, no de la constante «34 espacios». El catálogo
 * se edita desde `/admin` y el filtro de la consulta puede cambiar; un número
 * escrito aquí empezaría a mentir el día en que alguien dé de baja un espacio.
 */
function TarjetaEspaciosLibres({
  consulta,
  ahora,
}: {
  consulta: {
    data: Disponibilidad | undefined
    isPending: boolean
    isError: boolean
  }
  ahora: Date
}) {
  if (consulta.isPending) {
    return <TarjetaCargando etiqueta="Consultando los espacios de hoy…" />
  }

  if (consulta.isError || consulta.data === undefined) {
    return (
      <TarjetaDelTablero titulo="Espacios con lugar hoy">
        <p className="sin-dato">No se pudo consultar.</p>
        <p className="tablero__nota">
          Sin la respuesta del servidor no hay cuenta que dar; un cero aquí
          diría que no queda nada, que es distinto.
        </p>
      </TarjetaDelTablero>
    )
  }

  const datos = consulta.data

  if (!datos.abierto) {
    return (
      <TarjetaDelTablero titulo="Espacios con lugar hoy">
        <p className="sin-dato">Hoy no aplica</p>
        <p className="tablero__nota">
          El día está cerrado, así que no hay bloques que contar. Otra fecha
          puede tener.
        </p>
      </TarjetaDelTablero>
    )
  }

  if (datos.espacios.length === 0) {
    return (
      <TarjetaDelTablero titulo="Espacios con lugar hoy">
        <p className="sin-dato">Sin datos de espacios</p>
        <p className="tablero__nota">
          El día tiene horario, pero el servidor no devolvió ningún espacio. Eso
          no es normal: revisa que el catálogo esté cargado.
        </p>
      </TarjetaDelTablero>
    )
  }

  const conLugar = datos.espacios.filter((espacio) =>
    espacio.bloques.some(
      (bloque) =>
        bloque.estado === 'libre' && bloque.fin.getTime() > ahora.getTime(),
    ),
  ).length

  return (
    <TarjetaDelTablero titulo="Espacios con lugar hoy">
      <p className="tablero__cifra">
        {conLugar}
        <span className="tablero__de"> de {datos.espacios.length}</span>
      </p>
      {/* La tarjeta de al lado ya enlaza a los bloques de hoy: repetir el
          destino aquí solo añadiría un botón más que decide lo mismo. */}
      <p className="tablero__nota">
        {conLugar === 0
          ? 'Ningún espacio tiene bloques libres en lo que queda del día.'
          : 'Tienen al menos un bloque libre en lo que queda del día.'}
      </p>
    </TarjetaDelTablero>
  )
}

/* --- Los accesos ----------------------------------------------------------- */

/**
 * A dónde puede ir esta persona, según sus PERMISOS.
 *
 * NO ES UNA COPIA DEL MENÚ. El encabezado ofrece destinos; aquí cada uno viene
 * con qué se hace al llegar, que es lo que falta cuando alguien entra por
 * primera vez y ve seis nombres sin contexto.
 *
 * SE PREGUNTA POR PERMISOS, NUNCA POR EL NOMBRE DEL ROL (regla 8 del proyecto).
 * Un rol nuevo creado desde `/admin` —«coordinación», con `bloquear_espacios»—
 * ve su acceso sin que nadie toque TypeScript. Y esto NO autoriza nada: es para
 * no ofrecer lo que va a rebotar. Quien autoriza son las funciones de PostgreSQL
 * y la ruta del API.
 */
function Accesos({ perfil }: { perfil: Perfil | undefined }) {
  const usuario = perfil?.estado === 'registrado' ? perfil.usuario : null

  return (
    <section
      className="tarjeta seccion-portada"
      aria-labelledby="titulo-accesos"
    >
      <h2 className="tarjeta__titulo" id="titulo-accesos">
        Lo que puedes hacer
      </h2>

      <ul className="accesos">
        {/* Consultar no exige sesión y así debe seguir: se puede mirar qué hay
            antes de decidir si uno se registra. */}
        <li className="acceso">
          <Link
            to="/disponibilidad"
            search={{ fecha: fechaDeHoy() }}
            className="boton boton--primario"
          >
            Consultar la disponibilidad
          </Link>
          <p className="acceso__que">
            Un día, espacio por espacio, con sus bloques de una hora. Es de donde
            se reserva.
          </p>
        </li>

        <li className="acceso">
          <Link to="/espacios" className="boton">
            Catálogo de espacios
          </Link>
          <p className="acceso__que">
            Qué espacios existen, de qué tipo son y cuántos lugares tiene cada
            uno.
          </p>
        </li>

        {usuario !== null && (
          <li className="acceso">
            <Link to="/mis-reservaciones" className="boton">
              Mis reservaciones
            </Link>
            <p className="acceso__que">
              Lo tuyo, con su estado. Es el único sitio desde donde se cancela.
            </p>
          </li>
        )}

        {/* Quien reserva NO tiene este permiso aunque sea el dueño de la
            reservación: el control de entrada lo constata el mostrador. */}
        {usuario !== null && puedeAtenderMostrador(usuario) && (
          <li className="acceso">
            <Link to="/mostrador" className="boton">
              Mostrador
            </Link>
            <p className="acceso__que">
              El turno de hoy: entradas, salidas y llaves de quien llega.
            </p>
          </li>
        )}

        {/* Las dos mitades de la figura del docente abren la misma pantalla, y
            cada una se comprueba por separado: se puede acompañar alumnos sin
            responder por ningún espacio, y al revés. */}
        {usuario !== null &&
          (tienePermiso(usuario, 'acompanar_alumnos') ||
            tienePermiso(usuario, 'responsable_de_espacio')) && (
            <li className="acceso">
              <Link to="/mis-alumnos" className="boton">
                Mis alumnos y espacios
              </Link>
              <p className="acceso__que">
                A quién acompañas y de qué espacios respondes, con lo que
                reservaron hoy.
              </p>
            </li>
          )}

        {/* `puedeAdministrar` es «alguno de los cinco permisos», no «los
            cinco»: dentro, cada sección comprueba el suyo. */}
        {usuario !== null && puedeAdministrar(usuario) && (
          <li className="acceso">
            <Link to="/admin" className="boton">
              Administración
            </Link>
            <p className="acceso__que">
              Horarios, días sin servicio, catálogo, roles y docentes. Los datos
              que la escuela carga sin tocar el código.
            </p>
          </li>
        )}
      </ul>
    </section>
  )
}

/* --- Ayudantes -------------------------------------------------------------- */

/**
 * El reloj del tablero.
 *
 * HACE FALTA UNO DE VERDAD, no `new Date()` en el render: «lo que queda del
 * día» y «esta reservación sigue viva» son comparaciones contra la hora actual,
 * y sin un temporizador una pestaña abierta desde la mañana seguiría diciendo
 * que hay bloques libres a las ocho de la mañana.
 */
function useAhora(intervaloMs: number): Date {
  const [ahora, setAhora] = useState(() => new Date())

  useEffect(() => {
    const id = window.setInterval(() => setAhora(new Date()), intervaloMs)
    return () => window.clearInterval(id)
  }, [intervaloMs])

  return ahora
}

/** La vigente más próxima; `null` si no hay ninguna. Ver `TarjetaMiReservacion`. */
function proximaVigente(
  reservaciones: MiReservacion[],
  ahora: Date,
): MiReservacion | null {
  const vigentes = reservaciones
    .filter(
      (reservacion) =>
        (reservacion.estado === 'confirmada' ||
          reservacion.estado === 'en_curso') &&
        reservacion.fin.getTime() > ahora.getTime(),
    )
    .sort((a, b) => a.inicio.getTime() - b.inicio.getTime())

  return vigentes[0] ?? null
}

/** `09:00:00` → `09:00`. Los segundos de una hora de apertura son ruido. */
function horaCorta(valor: string): string {
  const partes = valor.split(':')
  const hora = partes[0]
  const minuto = partes[1]
  if (hora === undefined || minuto === undefined) return valor
  return `${hora}:${minuto}`
}

/**
 * Estilos de la portada, con el mismo patrón que `Ayuda`, `Tour` y `Esqueleto`:
 * `precedence` monta una sola copia en el `<head>`. Ni un color literal.
 *
 * VIVEN AQUÍ Y NO EN `global.css` también por una razón de esta semana: el
 * archivo global lo está editando otra persona en paralelo, y dos manos en el
 * mismo archivo es de donde salen los conflictos que nadie revisa con calma.
 */
function ESTILOS() {
  return (
    <style href="cubiculos-inicio" precedence="componente">{`
      /* --- El tablero ---------------------------------------------------- */

      /* Auto-fit y no un número fijo de columnas: en un teléfono queda una
         tarjeta por renglón, en una tableta dos y en escritorio cuatro, sin una
         sola consulta de medios. El mínimo es el ancho al que la cifra y su
         nota siguen cabiendo sin partirse. */
      .tablero {
        list-style: none;
        margin: 0;
        padding: 0;
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
        gap: var(--esp-4);
      }

      /* Cada celda de la rejilla es un elemento de lista, y hay dos formas de
         llenarlo: o el propio li ES la tarjeta —y entonces la rejilla ya lo
         estira a la altura del renglón—, o dentro va un esqueleto, que trae su
         propia tarjeta. En ese segundo caso hay que estirar al hijo; si no, la
         barra gris queda más baja que las tarjetas de al lado y el tablero pega
         un salto al cargar, que es justo lo que el esqueleto viene a evitar.

         La columna NO es decorativa: sin ella, el li que es tarjeta pondría su
         título y su texto uno al lado del otro. */
      .tablero > li {
        display: flex;
        flex-direction: column;
      }

      .tablero > li > .tarjeta { flex: 1; }

      /* El rótulo de la tarjeta es lo secundario: lo que se lee de lejos es la
         cifra o el dato, no el título que lo nombra. */
      .tablero__titulo {
        font-size: var(--texto-sm);
        font-weight: 600;
        color: var(--texto-tenue);
        margin-bottom: var(--esp-2);
      }

      .tablero__cifra {
        font-size: var(--texto-xl);
        line-height: var(--linea-titulo);
        color: var(--texto-fuerte);
        /* Cifras de ancho fijo: al refrescarse el número no descoloca la nota
           que va debajo. */
        font-variant-numeric: tabular-nums;
      }

      /* «de 34» acompaña a la cifra sin competir con ella. */
      .tablero__de {
        font-size: var(--texto-md);
        color: var(--texto-tenue);
        font-weight: 400;
      }

      .tablero__dato {
        font-size: var(--texto-md);
        font-weight: 600;
        color: var(--texto-fuerte);
        text-wrap: pretty;
      }

      /* La clave del espacio (CUB-14) en monoespaciada: es un identificador
         que alguien va a comparar carácter por carácter con un letrero. */
      .tablero__clave {
        font-family: var(--fuente-mono);
        font-size: var(--texto-sm);
        font-weight: 400;
        color: var(--texto-tenue);
      }

      .tablero__nota {
        margin-top: var(--esp-2);
        font-size: var(--texto-sm);
        color: var(--texto-tenue);
        text-wrap: pretty;
      }

      .tablero__acciones { margin-top: var(--esp-3); }

      .tablero__grito { margin-bottom: var(--esp-4); }

      .tablero-envoltura { margin-bottom: var(--esp-5); }

      /* --- Los accesos --------------------------------------------------- */

      .accesos {
        list-style: none;
        margin: 0;
        padding: 0;
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(16rem, 1fr));
        gap: var(--esp-4);
      }

      .acceso__que {
        margin-top: var(--esp-2);
        font-size: var(--texto-sm);
        color: var(--texto-tenue);
        max-width: var(--medida-lectura);
        text-wrap: pretty;
      }

      /* --- Cómo se usa ---------------------------------------------------- */

      /* Numerada de verdad, no una lista con viñetas: el orden es la mitad de
         lo que estos cuatro renglones explican. */
      .pasos-uso {
        margin: 0;
        padding-left: var(--esp-5);
        display: flex;
        flex-direction: column;
        gap: var(--esp-4);
        max-width: var(--medida-lectura);
        text-wrap: pretty;
      }

      .pasos-uso li { padding-left: var(--esp-1); }

      /* Lo que hay que hacer, separado de por qué. Se lee de un vistazo saltando
         solo estas cuatro frases. */
      .pasos-uso__que {
        font-weight: 600;
        color: var(--texto-fuerte);
      }

      /* El botón en su propio renglón: dentro del párrafo empuja el texto y
         parte la frase en dos trozos que no se leen seguidos. */
      .pasos-uso__accion {
        display: block;
        margin-top: var(--esp-3);
      }

      .seccion-portada { margin-top: var(--esp-5); }
    `}</style>
  )
}
